// The UK's four constituent countries as separate polygons (extracted from
// Natural Earth 50m map-subunits). We treat them as distinct "countries" so a
// place in Wales and a place in England count — and fill — separately, even
// though they share ISO country code "GB".

export interface UkNation {
  code: string; // ISO 3166-2 GB subdivision, e.g. "GB-ENG"
  name: string; // Swedish display name
  continent: string; // always "Europe"
}

export const UK_NATIONS: Record<string, UkNation> = {
  "GB-ENG": { code: "GB-ENG", name: "England", continent: "Europe" },
  "GB-SCT": { code: "GB-SCT", name: "Skottland", continent: "Europe" },
  "GB-WLS": { code: "GB-WLS", name: "Wales", continent: "Europe" },
  "GB-NIR": { code: "GB-NIR", name: "Nordirland", continent: "Europe" },
};

export const UK_CODES = Object.keys(UK_NATIONS);
export const isUkCode = (code: string) => code.startsWith("GB-");

// Served same-origin from public/ (no CORS), ~25 KB.
const URL = process.env.PUBLIC_URL + "/uk-subunits.geojson";

let cache: Promise<any> | null = null;
export const loadUkSubunits = () => {
  if (!cache) {
    cache = fetch(URL).then((r) => {
      if (!r.ok) throw new Error("Kunde inte ladda UK-gränser");
      return r.json();
    });
  }
  return cache;
};

// Ray-casting point-in-polygon over GeoJSON coordinates ([lng, lat]).
const pointInRing = (lng: number, lat: number, ring: number[][]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const pointInPolygon = (lng: number, lat: number, rings: number[][][]): boolean => {
  if (!pointInRing(lng, lat, rings[0])) return false; // outside outer ring
  for (let i = 1; i < rings.length; i++) {
    if (pointInRing(lng, lat, rings[i])) return false; // inside a hole
  }
  return true;
};

const pointInGeometry = (lng: number, lat: number, geom: any): boolean => {
  if (geom.type === "Polygon") return pointInPolygon(lng, lat, geom.coordinates);
  if (geom.type === "MultiPolygon")
    return geom.coordinates.some((poly: number[][][]) => pointInPolygon(lng, lat, poly));
  return false;
};

// Which UK nation a coordinate falls in, e.g. "GB-SCT". null if in none.
export const ukNationCode = async (lat: number, lng: number): Promise<string | null> => {
  const data = await loadUkSubunits();
  const hit = data.features.find((f: any) => pointInGeometry(lng, lat, f.geometry));
  return hit ? hit.properties.code : null;
};
